<template>
  <div class="diagnosis-chooser relative" ref="root" style="min-width: 350px">
    <label class="input input-bordered flex items-center gap-2 w-full">
      <input
        v-model="query"
        type="text"
        class="grow"
        placeholder="Add diagnosis"
        @focus="open = true"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="onEnter"
        @keydown.esc="open = false"
      />
      <span v-if="loading" class="loading loading-spinner loading-xs"></span>
    </label>

    <ul
      v-if="open && (flatResults.length || query.trim())"
      class="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-box border border-base-300 bg-base-200 shadow-lg py-1"
    >
      <template v-for="group in groups" :key="group.lexicon">
        <li
          v-if="group.entries.length"
          class="px-3 pt-2 pb-1 text-xs font-semibold uppercase text-base-content/50"
        >
          {{ group.label }}
        </li>
        <li
          v-for="entry in group.entries"
          :key="group.lexicon + entry.code"
          class="flex gap-2 px-3 py-1.5 cursor-pointer text-base-content hover:bg-base-300"
          :class="{ 'bg-base-300': highlighted === entry._idx }"
          @mousedown.prevent="select(entry)"
          @mouseenter="highlighted = entry._idx"
        >
          <span class="grow whitespace-normal">{{ entry.text }}</span>
          <span class="opacity-50 text-right shrink-0">{{ entry.code }}</span>
        </li>
      </template>
      <li
        v-if="query.trim()"
        class="px-3 py-1.5 cursor-pointer text-base-content hover:bg-base-300"
        :class="{ 'bg-base-300': highlighted === flatResults.length }"
        @mousedown.prevent="selectCustom"
        @mouseenter="highlighted = flatResults.length"
      >
        Use "{{ query.trim() }}" as custom diagnosis
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import type Diagnosis from "../../types/Diagnosis";

interface Entry {
  code: string;
  text: string;
  _idx: number;
}
interface Group {
  lexicon: string;
  label: string;
  entries: Entry[];
}

const emit = defineEmits<{ select: [Diagnosis] }>();

const root = ref<HTMLElement | null>(null);
const query = ref("");
const open = ref(false);
const loading = ref(false);
const highlighted = ref(0);
const groups = ref<Group[]>([]);

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
let lastSearched = "";

// Flat list of selectable entries (group headers excluded) for keyboard nav.
const flatResults = computed(() => groups.value.flatMap((g) => g.entries));

async function search(q: string) {
  if (!q) {
    groups.value = [];
    return;
  }
  try {
    loading.value = true;
    const data = await fetch(
      `https://lex.orionmd.com/search?q=${encodeURIComponent(q)}`
    ).then((r) => r.json());
    const { result } = data as {
      result: { lexicon: string; doc: { i: string; d: string } }[];
    };

    const radlex: Entry[] = [];
    const icd10: Entry[] = [];
    let idx = 0;
    for (const { lexicon, doc } of result) {
      const entry = { code: doc.i, text: doc.d, _idx: idx++ };
      if (lexicon === "radlex") radlex.push(entry);
      else if (lexicon === "icd10") icd10.push(entry);
    }
    groups.value = [
      { lexicon: "radlex", label: "RadLex", entries: radlex },
      { lexicon: "icd10", label: "ICD10", entries: icd10 },
    ];
    highlighted.value = 0;
  } catch (err) {
    console.error("Diagnosis search failed", err);
    groups.value = [];
  } finally {
    loading.value = false;
  }
}

// Debounced search whenever the query changes.
watch(query, () => {
  open.value = true;
  clearTimeout(debounceTimer);
  const q = query.value.trim();
  if (q === lastSearched) return;
  debounceTimer = setTimeout(() => {
    lastSearched = q;
    search(q);
  }, 250);
});

function select(entry: Entry) {
  emit("select", { code: entry.code || "custom", text: entry.text, certainty: null });
  reset();
}

function selectCustom() {
  const text = query.value.trim();
  if (!text) return;
  emit("select", { code: "custom", text, certainty: null });
  reset();
}

function reset() {
  query.value = "";
  groups.value = [];
  lastSearched = "";
  highlighted.value = 0;
  open.value = false;
}

function move(delta: number) {
  open.value = true;
  const max = flatResults.value.length; // last index is the "custom" option
  highlighted.value = Math.min(Math.max(highlighted.value + delta, 0), max);
}

function onEnter() {
  if (highlighted.value < flatResults.value.length) {
    select(flatResults.value[highlighted.value]);
  } else {
    selectCustom();
  }
}

function onClickOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false;
}

onMounted(() => document.addEventListener("click", onClickOutside));
onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutside);
  clearTimeout(debounceTimer);
});
</script>
